import { useState } from "react";
import { Link } from "react-router-dom";
import { MarketingLayout } from "../components/MarketingLayout";
import Field from "../components/Field";
import HeroStudio from "../components/visuals/HeroStudio";
import api, { errMsg } from "../lib/api";
import { ArrowRight, Check } from "lucide-react";

export default function Waitlist() {
  const [form, setForm] = useState({ email: "", name: "", company: "", use_case: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((x) => ({ ...x, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    // Validate inline rather than in a toast: a toast is not associated
    // with the field, auto-dismisses, and leaves the user no way back to
    // what is wrong.
    const next = {};
    if (!form.name.trim()) next.name = "Tell us what to call you.";
    if (!form.email.trim()) next.email = "Enter the email you'd like your invite sent to.";
    setErrors(next);
    if (Object.keys(next).length) {
      // Focus the first invalid field in DOM order, not insertion order —
      // otherwise the user is dropped past the field they need to fix.
      const firstInvalid = ["wl-name", "wl-email"].find(
        (id) => next[id === "wl-name" ? "name" : "email"]
      );
      document.getElementById(firstInvalid)?.focus();
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/waitlist", { ...form, referrer: document.referrer || "" });
      setDone(r.data?.status === "existing_account" ? "existing" : "joined");
    } catch (err) {
      setErrors({ email: errMsg(err, "Something went wrong. Please try again.") });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <MarketingLayout>
        <section className="max-w-2xl mx-auto px-6 md:px-10 py-24">
          {/* role=status so the outcome is announced, not just shown */}
          <div className="brutal-card p-10" role="status" aria-live="polite">
            <span className="brutal-chip brutal-chip-success mb-6">
              <Check size={12} aria-hidden="true" /> {done === "existing" ? "Account found" : "You're on the list"}
            </span>
            <h1 className="font-display font-black type-title tracking-tighter mb-4">
              {done === "existing" ? "You already have an account." : "Thanks — you're in the queue."}
            </h1>
            {done === "existing" ? (
              <p className="text-ink-muted mb-8">
                That email is already registered. Sign in and pick up where you left off.
              </p>
            ) : (
              <p className="text-ink-muted mb-8">
                We're letting people in gradually so every render gets the capacity it
                needs. When it's your turn we'll email <strong className="text-ink">{form.email}</strong> a
                link to create your account. No password needed until then.
              </p>
            )}
            <Link to={done === "existing" ? "/login" : "/"} className="brutal-btn">
              {done === "existing" ? "Sign in" : "Back to home"} <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <section className="max-w-wide mx-auto px-6 md:px-10 py-14 md:py-20 grid md:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className="md:col-span-6">
          <p className="marquee-tag mb-6">// INVITE-ONLY</p>
          <h1 className="font-display font-black type-title tracking-tighter mb-6">
            Request<br />early access.
          </h1>
          <p className="type-lead text-ink-muted max-w-prose mb-8">
            We're onboarding gradually so every render has the capacity it needs.
            Tell us a little about what you're building and we'll email you an
            invite when a place opens up.
          </p>

          <form onSubmit={submit} className="space-y-5 max-w-md" data-testid="waitlist-form" noValidate>
            <Field label="Your name" error={errors.name} required>
              <input id="wl-name" className="brutal-input" autoComplete="name"
                     value={form.name} onChange={set("name")} data-testid="waitlist-name" />
            </Field>
            <Field label="Work email" error={errors.email} required
                   hint="This is where your invite will be sent.">
              <input id="wl-email" type="email" className="brutal-input" autoComplete="email"
                     value={form.email} onChange={set("email")} data-testid="waitlist-email" />
            </Field>
            <Field label="Company" hint="Optional.">
              <input className="brutal-input" autoComplete="organization"
                     value={form.company} onChange={set("company")} data-testid="waitlist-company" />
            </Field>
            <Field label="What would you use it for?" hint="Optional — it helps us prioritise.">
              <textarea className="brutal-input min-h-[100px]" value={form.use_case}
                        onChange={set("use_case")} data-testid="waitlist-use-case" />
            </Field>

            <button type="submit" disabled={loading}
                    className="brutal-btn brutal-btn-lg w-full justify-center"
                    data-testid="waitlist-submit">
              {loading ? "Joining…" : "Join the waitlist"}
              {!loading && <ArrowRight size={16} aria-hidden="true" />}
            </button>
            <p className="font-mono text-xs text-ink-muted">
              No account, no password, no card. Already have an account?{" "}
              <Link to="/login" className="text-ink-accent underline underline-offset-4 hover:no-underline">Sign in</Link>.
            </p>
          </form>
        </div>

        <div className="md:col-span-6 md:mt-4">
          <HeroStudio />
        </div>
      </section>
    </MarketingLayout>
  );
}
